import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, Car, Shield, User, Phone, Mail } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const DriverRegister = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', license_number: '', experience: '', emergency_contact: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/drivers/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
      } else {
        toast({ title: 'Error', description: data.detail || 'Registration failed', variant: 'destructive' });
      }
    } catch { toast({ title: 'Error', description: 'Something went wrong', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="registration-success">
        <Card className="max-w-md w-full p-8 text-center bg-white">
          <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-black mb-2">Application Submitted!</h2>
          <p className="text-gray-600 mb-6">Thank you for your interest in driving with ACE. Our team will review your application and get back to you within 48 hours.</p>
          <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">Return to ACE</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="driver-register">
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <div className="h-16 w-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Car className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Drive with ACE</h1>
          <p className="text-gray-400">Join Atlanta's premier luxury transportation team</p>
        </div>

        <Card className="bg-white p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="flex items-center gap-2"><User className="h-4 w-4 text-gray-400" />Full Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="John Smith" data-testid="reg-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2"><Mail className="h-4 w-4 text-gray-400" />Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="john@example.com" data-testid="reg-email" />
              </div>
              <div>
                <Label className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400" />Phone *</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required placeholder="(404) 555-1234" data-testid="reg-phone" />
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-2"><Shield className="h-4 w-4 text-gray-400" />Driver's License Number</Label>
              <Input value={form.license_number} onChange={e => setForm({...form, license_number: e.target.value})} placeholder="DL Number" data-testid="reg-license" />
            </div>
            <div>
              <Label>Driving Experience</Label>
              <Input value={form.experience} onChange={e => setForm({...form, experience: e.target.value})} placeholder="e.g. 5 years professional driving, CDL holder" data-testid="reg-experience" />
            </div>
            <div>
              <Label>Emergency Contact</Label>
              <Input value={form.emergency_contact} onChange={e => setForm({...form, emergency_contact: e.target.value})} placeholder="Name & phone number" data-testid="reg-emergency" />
            </div>

            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
              <p className="font-semibold text-black mb-2">What to expect:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Application reviewed within 48 hours</li>
                <li>Background check & driving record verification</li>
                <li>Orientation & vehicle familiarization</li>
                <li>Competitive hourly rates + tips</li>
              </ul>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 h-12 text-base" data-testid="submit-registration">
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default DriverRegister;
